import { IproductRegistration } from "../models/epcCustomer.model";

// Update: Allow string | null | undefined
function normalizeText(s?: string | null) {
  return (s ?? "").trim().replace(/\s+/g, " ").toLowerCase();
}

// Update: Allow string | null | undefined
function normalizeConsumerNumber(s?: string | null) {
  return (s ?? "").toString().replace(/\s+/g, "").trim();
}

// Update: Allow string[] | null | undefined
function normalizeArray(arr?: string[] | null) {
  return (arr ?? []).map((s) => String(s ?? "").trim()).filter(Boolean);
}

// Update: Allow string[] | null | undefined
function arraysEqual(a?: string[] | null, b?: string[] | null) {
  const A = normalizeArray(a);
  const B = normalizeArray(b);
  if (A.length !== B.length) return false;
  // Sort before comparing to ensure order doesn't matter (optional but recommended)
  const sortedA = [...A].sort();
  const sortedB = [...B].sort();

  for (let i = 0; i < sortedA.length; i++) {
    if (sortedA[i] !== sortedB[i]) return false;
  }
  return true;
}

export default function hasChanges(
  original: IproductRegistration | undefined | null,
  updated: IproductRegistration,
  newGeoFile?: File | null,
): boolean {
  if (newGeoFile) return true;

  const getDayStr = (d: any) => {
    if (!d) return "";
    const date = new Date(d);
    return isNaN(date.getTime()) ? "" : date.toISOString().split("T")[0];
  };

  const origDate = getDayStr((original as any)?.installationDate);
  const updDate = getDayStr(updated.installationDate);
  if (origDate !== updDate) return true;

  // --- Basic Fields ---

  const origName = normalizeText(original?.name);
  const updName = normalizeText(updated?.name);
  if (origName !== updName) return true;

  const origConsumer = normalizeConsumerNumber(original?.consumerNumber);
  const updConsumer = normalizeConsumerNumber(updated?.consumerNumber);
  if (origConsumer !== updConsumer) return true;

  const origAddress = normalizeText(original?.address);
  const updAddress = normalizeText(updated?.address);
  if (origAddress !== updAddress) return true;

  const origArea = normalizeText(original?.area);
  const updArea = normalizeText(updated?.area);
  if (origArea !== updArea) return true;

  // --- Solar Panel Fields ---

  const origRegNum = normalizeText((original as any)?.registrationNumber);
  const updRegNum = normalizeText((updated as any)?.registrationNumber);
  if (origRegNum !== updRegNum) return true;

  const origPanelMake = normalizeText(original?.panelMake);
  const updPanelMake = normalizeText(updated.panelMake);
  if (origPanelMake !== updPanelMake) return true;

  const origPanelCap = normalizeText(original?.panelCapacity);
  const updPanelCap = normalizeText(updated.panelCapacity);
  if (origPanelCap !== updPanelCap) return true;

  const origModules = normalizeText(original?.numberOfModules);
  const updModules = normalizeText(updated.numberOfModules);
  if (origModules !== updModules) return true;

  const origTotalSolar = normalizeText(original?.totalSolarCapacity);
  const updTotalSolar = normalizeText(updated.totalSolarCapacity);
  if (origTotalSolar !== updTotalSolar) return true;

  // --- Inverter Fields ---

  const origPanelVolt = normalizeText((original as any)?.panelVoltage);
  const updPanelVolt = normalizeText((updated as any)?.panelVoltage);
  if (origPanelVolt !== updPanelVolt) return true;

  const origInvMake = normalizeText(original?.inverterMake);
  const updInvMake = normalizeText(updated.inverterMake);
  if (origInvMake !== updInvMake) return true;

  const origInvCap = normalizeText(original?.inverterCapacity);
  const updInvCap = normalizeText(updated.inverterCapacity);
  if (origInvCap !== updInvCap) return true;

  // --- Barcodes ---

  const origInvVolt = normalizeText((original as any)?.inverterVoltage);
  const updInvVolt = normalizeText((updated as any)?.inverterVoltage);
  if (origInvVolt !== updInvVolt) return true;

  if (!arraysEqual(original?.panelBarcodes, updated.panelBarcodes)) {
    return true;
  }

  if (!arraysEqual(original?.inverterBarcodes, updated.inverterBarcodes)) {
    return true;
  }

  return false;
}
